/**
 * Subcontract Order Service
 *
 * Handles all subcontract (外协) order operations
 */

import httpClient from './httpClient';

const subcontractService = {
  // ==================== List & Search ====================

  async getSubcontractOrders(params = {}) {
    try {
      const response = await httpClient.get('/api/subcontract-orders', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching subcontract orders:', error);
      throw error;
    }
  },

  async getSubcontractStats() {
    try {
      const response = await httpClient.get('/api/subcontract-orders/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching subcontract stats:', error);
      throw error;
    }
  },

  // ==================== CRUD ====================

  async getSubcontractOrder(orderId) {
    try {
      const response = await httpClient.get(`/api/subcontract-orders/${orderId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching subcontract order:', error);
      throw error;
    }
  },

  async createSubcontractOrder(data) {
    try {
      const response = await httpClient.post('/api/subcontract-orders', data);
      return response.data;
    } catch (error) {
      console.error('Error creating subcontract order:', error);
      throw error;
    }
  },

  async updateSubcontractOrder(orderId, data) {
    try {
      const response = await httpClient.put(`/api/subcontract-orders/${orderId}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating subcontract order:', error);
      throw error;
    }
  },

  async deleteSubcontractOrder(orderId) {
    try {
      const response = await httpClient.delete(`/api/subcontract-orders/${orderId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting subcontract order:', error);
      throw error;
    }
  },

  // ==================== Lifecycle ====================

  async issueMaterials(orderId, payload) {
    try {
      // New flow: create material requisition for approval
      const data = Array.isArray(payload) ? { materials: payload } : payload;
      const response = await httpClient.post(
        `/api/subcontract-orders/${orderId}/issue-materials`,
        data
      );
      return response.data;
    } catch (error) {
      console.error('Error creating material requisition for subcontract:', error);
      throw error;
    }
  },

  async markInProgress(orderId) {
    try {
      const response = await httpClient.post(`/api/subcontract-orders/${orderId}/start`);
      return response.data;
    } catch (error) {
      console.error('Error marking subcontract in progress:', error);
      throw error;
    }
  },

  async receiveCompletion(orderId, itemCompletions, movementReturnId, settlementData, leftoverData) {
    try {
      const response = await httpClient.post(`/api/subcontract-orders/${orderId}/receive`, {
        item_completions: itemCompletions,
        movement_return_id: movementReturnId,
        settlement: settlementData,
        leftover_data: leftoverData
      });
      return response.data;
    } catch (error) {
      console.error('Error receiving subcontract completion:', error);
      throw error;
    }
  },

  async cancelOrder(orderId) {
    try {
      const response = await httpClient.post(`/api/subcontract-orders/${orderId}/cancel`);
      return response.data;
    } catch (error) {
      console.error('Error cancelling subcontract order:', error);
      throw error;
    }
  },

  async settleOrder(orderId, settlementAmount) {
    try {
      const response = await httpClient.post(`/api/subcontract-orders/${orderId}/settle`, {
        settlement_amount: settlementAmount
      });
      return response.data;
    } catch (error) {
      console.error('Error settling subcontract order:', error);
      throw error;
    }
  },

  async receiveInbound(orderId, items, location = 'main', notes = '') {
    try {
      const response = await httpClient.post(`/api/subcontract-orders/${orderId}/inbound`, {
        items,
        location,
        notes
      });
      return response.data;
    } catch (error) {
      console.error('Error receiving subcontract inbound:', error);
      throw error;
    }
  },

  // ==================== Production Order Integration ====================

  async createStepSubcontract(orderId, itemIndex, stepSequence, data) {
    try {
      const response = await httpClient.post(
        `/api/production-orders/${orderId}/items/${itemIndex}/steps/${stepSequence}/subcontract`,
        data
      );
      return response.data;
    } catch (error) {
      console.error('Error creating step subcontract:', error);
      throw error;
    }
  },

  async getPendingSubcontractSteps(stepName) {
    try {
      const params = stepName ? { step_name: stepName } : {};
      const response = await httpClient.get('/api/production-orders/pending-subcontract-steps', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching pending subcontract steps:', error);
      throw error;
    }
  }
};

export default subcontractService;
